import { compact } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { OrganizationType } from 'enums';
import { useBoolean, useMutateMarket, useMutateOrganization, useMutateUserMarket, useToast } from 'hooks';
import { messages } from 'i18n';
import { dialogTransitionDurationValue } from 'themes';
import { Market, UserMarket, UserMarketCreate } from 'types';
import { UserMarketFormProps } from './types';
import { useUpdateUserMarketContacts } from './useUpdateUserMarketContacts';
import { FormMode } from './utils';

interface ResponseAsError {
  message: string;
  statusCode: number;
}

interface SubmitUserMarketProps {
  id: string | null;
  newMarketOrganizationType?: OrganizationType;
  mode: FormMode;
  defaultValues: UserMarketFormProps;
  methods: UseFormReturn<UserMarketFormProps, object>;
  onMarketCreated?: (market: UserMarket) => void;
  closeWithoutPrompt: () => void;
  markets: Market[];
}

export function useSubmitUserMarket({
  id,
  mode,
  defaultValues,
  methods,
  onMarketCreated,
  closeWithoutPrompt,
  newMarketOrganizationType,
  markets,
}: SubmitUserMarketProps) {
  const [isSubmitting, { on: startSubmit, off: stopSubmit }] = useBoolean();

  const { createUserMarket, updateUserMarket } = useMutateUserMarket();
  const { createMarketByName, invalidateMarkets } = useMutateMarket();
  const { updateOrganization } = useMutateOrganization();
  const { areContactEmailsValid, updateContacts } = useUpdateUserMarketContacts({ defaultValues, methods });

  const { showDelayedToast, showToast } = useToast();

  const afterSubmit = (data: UserMarket | null) => {
    showDelayedToast(
      'success',
      {
        message:
          mode === FormMode.Update
            ? messages.userMarketModal.userMarketUpdated(data?.marketName ?? '')
            : messages.userMarketModal.userMarketAdded(data?.marketName ?? ''),
      },
      dialogTransitionDurationValue,
    );
    if (data) {
      onMarketCreated?.(data);
    }

    closeWithoutPrompt();
  };

  async function createNewUserMarket(userMarket: UserMarketCreate, marketName: string) {
    try {
      const response = await createUserMarket.mutateAsync({ data: userMarket });
      const responseAsError = { ...response } as ResponseAsError;

      if (responseAsError?.statusCode === 409) {
        showToast('warning', { message: messages.userMarketModal.userMarketAlreadyExist(marketName) });
      } else {
        afterSubmit(response);
      }
    } catch {
      stopSubmit();
    }
  }

  async function renameOrganization(organizationId: string, marketName: string) {
    const response = await updateOrganization.mutateAsync({
      id: organizationId,
      data: { name: marketName },
    });
    await invalidateMarkets();

    const responseAsError = { ...response } as ResponseAsError;

    if (responseAsError?.statusCode === 409) {
      showToast('warning', { message: messages.userMarketModal.userMarketAlreadyExist(marketName) });
      // Throw the error to stop the submit process
      throw new Error(responseAsError.message);
    }
  }

  async function submitUserMarket(data: UserMarketFormProps) {
    startSubmit();

    try {
      let { organizationId, marketId, marketName } = defaultValues;

      if (mode === FormMode.Update) {
        // In update mode, if the market name has changed, we need to update the organization name first
        if (data.marketName !== defaultValues.marketName && !!defaultValues.organizationId) {
          await renameOrganization(defaultValues.organizationId, data.marketName);
        }
      }

      if (mode === FormMode.Create) {
        const existingMarket = markets.find((marketItem) => marketItem.id === data.organizationId);
        // if form organizationId is not in the list it means the user created a new option in the form auto complete and the id is actually the name of the organization market that we need to create
        if (!existingMarket) {
          // this create call is an upsert, so it will only return the existing market if it already exists
          const market = await createMarketByName.mutateAsync({
            data: {
              name: data.organizationId,
              organizationType: newMarketOrganizationType,
            },
          });
          organizationId = market!.organizationId;
          marketId = market!.id;
          marketName = market!.organizationName;
        } else {
          organizationId = existingMarket.organizationId;
          marketId = existingMarket.id;
          marketName = existingMarket.organizationName;
        }
      }

      const { contacts, ...rest } = data;
      const touchedFieldsContacts = methods.formState.touchedFields.contacts ?? [];
      const dirtyFieldsContacts = methods.formState.dirtyFields.contacts ?? [];

      if (await areContactEmailsValid(contacts, touchedFieldsContacts)) {
        const contactIds = await updateContacts(contacts, dirtyFieldsContacts, organizationId);

        const userMarket: UserMarketCreate = {
          ...rest,
          marketId,
          contactIds: compact(contactIds),
        };

        if (mode === FormMode.Update && id) {
          try {
            await updateUserMarket.mutateAsync({ id, data: userMarket }, { onSuccess: afterSubmit });
          } catch {
            stopSubmit();
          }
        } else {
          await createNewUserMarket(userMarket, marketName);
        }
      }
    } finally {
      stopSubmit();
    }
  }

  return { submitUserMarket, isSubmitting };
}
